import React, { useEffect, useState, useRef } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Tabs,
  Tab,
  Box,
  makeStyles,
  createStyles,
} from "@material-ui/core";
import _, { split } from "lodash";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import { buttonColors } from "../../../../utils/enum";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import IncrementDecrementTextboxNormal from "../../../../components/common/IncrementDecrementTextbox/IncrementDecrementTextboxNoraml";
import VoidLine from "./VoidLine";
import VoidOut from "./VoidOut";
import CardCommon from "../../../../components/card/CardCommon";
import { convertDateTimeFormat } from "../../../../utils/ConvertDateTimeFormat";
import JsonViewer from "./JsonViewer";
import { getIsAuthorized } from "../../../../utils/authorities";
import Authorities from "../../../../auth/authorities";

// Styles for tabs (matching the reference code)
const useStyles = makeStyles((theme: any) =>
  createStyles({
    root: {
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
    },
    tabPanel: {
      backgroundColor: theme.palette.background.default,
    },
    tabsContainer: {
      padding: "0 28px",
      borderBottom: `1px solid ${theme.palette.divider || "#e8e6df"}`,
    },
    jsonTab: {
      color: "#ff0000 !important",
      "&.Mui-selected": {
        color: "#ff0000 !important",
      },
      "&:hover": {
        color: "#cc0000 !important",
      },
    },
  }),
);

// Helper function to check if user has PREX_ authority
const isPREXUser = (): boolean => {
  try {
    const isAuthorized = getIsAuthorized(Authorities.STOCK_CONFIGURATION_READ);

    // Check if any authority starts with "PREX_"
    return isAuthorized?.toString() === "true" ? true : false;
  } catch (error) {
    console.error("Error checking PREX user:", error);
    return false;
  }
};

// ===================== SAFE JSON HELPER FUNCTIONS =====================

/**
 * JSON string එකක් ආරක්ෂිතව parse කරයි
 * @param data - parse කිරීමට අවශ්ය data
 * @param defaultValue - parse කිරීම අසාර්ථක වුවහොත් return කරන default value
 * @returns parsed data හෝ defaultValue
 */
const safeJsonParse = (data: any, defaultValue: any = {}): any => {
  // null හෝ undefined පරීක්ෂා කරන්න
  if (data === null || data === undefined) {
    return defaultValue;
  }

  // දැනටමත් Object හෝ Array එකක් නම්
  if (typeof data === "object") {
    return data;
  }

  // String එකක් නම් parse කරන්න
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (trimmed === "") {
      return defaultValue;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return parsed !== null && parsed !== undefined ? parsed : defaultValue;
    } catch (e) {
      console.warn("JSON Parse Error:", e);
      return defaultValue;
    }
  }

  return defaultValue;
};

/**
 * JSON data එක හිස්දැයි පරීක්ෂා කරයි
 */
const isEmptyJSONData = (data: any): boolean => {
  if (data === null || data === undefined) {
    return true;
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (trimmed === "") {
      return true;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === "object" && parsed !== null) {
        return Object.keys(parsed).length === 0;
      }
      return false;
    } catch (e) {
      // JSON නොවන string එකක් - හිස් නොවේ
      return false;
    }
  }

  if (typeof data === "object") {
    if (Array.isArray(data)) {
      return data.length === 0;
    }
    return Object.keys(data).length === 0;
  }

  return false;
};

// ✅ NEW: නිවැරදිව JSON string එකක්දැයි පරීක්ෂා කරයි
const isValidJSONString = (str: any): boolean => {
  if (!str || typeof str !== "string") {
    return false;
  }

  const trimmed = str.trim();
  if (trimmed === "") {
    return false;
  }

  try {
    JSON.parse(trimmed);
    return true;
  } catch (e) {
    return false;
  }
};

// ===================== END SAFE JSON HELPER FUNCTIONS =====================

// TabPanel component (matching the reference code)
interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  const classes = useStyles();

  return (
    <div
      role="tabpanel"
      className={classes.tabPanel}
      hidden={value !== index}
      id={`log-details-tab-${index}`}
      aria-labelledby={`log-details-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ padding: "16px 0" }}>{children}</div>}
    </div>
  );
};

function a11yProps(index: any) {
  return {
    id: `log-details-tab-${index}`,
    "aria-controls": `log-details-tab-${index}`,
  };
}

export interface VoidTransactionDetailsModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedData: any;
  userDetails: any;
  locationSelectorList: any;
  isGeneric: any;
  userName: any;
}

// Original function - kept unchanged for backward compatibility
function getValidJSONString(input: any) {
  if (typeof input !== "string" || input.trim() === "") return null;

  try {
    const parsed = JSON.parse(input);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      Object.keys(parsed).length > 0
    ) {
      return null;
    }
  } catch (e) {
    return input;
  }

  return input;
}

const LogDetailsModal: React.FC<VoidTransactionDetailsModalProps> = ({
  isOpen,
  setIsOpen,
  selectedData,
  userDetails,
  locationSelectorList,
  isGeneric,
  userName,
}) => {
  const classes = useStyles();
  const [voidData, setVoidDate] = useState<any>({});
  const [description, setDescription] = useState<any>("");
  const [kitchenPrintData, setKitchenPrintData] = useState<any>({});
  const [jsonData, setJsonData] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [paperHeight, setPaperHeight] = useState<string>("auto");
  const jsonContainerRef = useRef<HTMLDivElement>(null);

  // Check if user has PREX_ authority
  const [isPREX, setIsPREX] = useState<boolean>(false);

  useEffect(() => {
    // Check if user is PREX_ user
    const prexUser = isPREXUser();
    setIsPREX(prexUser);
  }, []);

  // Check if JSON data is available for the tab (only for PREX_ users)
  const hasJsonData = jsonData !== null && isPREX;

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
    // Reset height when switching tabs
    setPaperHeight("auto");
  };

  // ===================== FIXED useEffect =====================
  useEffect(() => {
    const descString = selectedData?.desc;

    if (selectedData?.type === "void") {
      // Safe parse void data
      const data = safeJsonParse(descString, {});
      setVoidDate(data);
      setKitchenPrintData({});
      setDescription("");
      setJsonData(null);
      setTabValue(0);
      setPaperHeight("auto");
    } else if (selectedData?.type === "kitchen-print") {
      console.log("selectedData:", selectedData);
      console.log("selectedData?.desc", descString);

      // Safe parse kitchen-print data
      const data = safeJsonParse(descString, {});
      setKitchenPrintData(data);

      // ✅ FIXED: නිවැරදි JSON validation
      // desc එක valid JSON නම් description hide කරන්න (data කොටසේ පෙන්වනවා)
      // desc එක plain text නම් description එකේ පෙන්වන්න
      if (
        descString &&
        typeof descString === "string" &&
        descString.trim() !== ""
      ) {
        // isValidJSONString() භාවිතා කරලා JSON ද කියලා හරියට check කරන්න
        setDescription(isValidJSONString(descString) ? "" : descString);
      } else {
        setDescription("");
      }

      setJsonData(null);
      setTabValue(0);
      setPaperHeight("auto");
    } else {
      // For other types - keep original behavior
      setDescription(getValidJSONString(descString));
      setKitchenPrintData({});
      setVoidDate({});

      // Check if selectedData.data is valid JSON and not empty
      if (selectedData?.data && isPREX) {
        // Check if data is valid JSON
        const isValid = !isEmptyJSONData(selectedData.data);
        setJsonData(isValid ? selectedData.data : null);
      } else {
        setJsonData(null);
      }
      setTabValue(0);
      setPaperHeight("auto");
    }
  }, [selectedData, isPREX]);
  // ===================== END FIXED useEffect =====================

  // Adjust dialog height when JSON tab is selected and content is rendered
  useEffect(() => {
    if (tabValue === 1 && hasJsonData) {
      // Use a timeout to ensure the DOM has fully rendered and measured
      const timer = setTimeout(() => {
        if (jsonContainerRef.current) {
          const jsonHeight = jsonContainerRef.current.scrollHeight;
          // Add approximate height for header, tabs, padding, and footer
          const extraHeight = 280;
          const totalHeight = jsonHeight + extraHeight;

          // Cap at 98vh to prevent it from going completely off-screen,
          // while effectively eliminating internal scrolling for typical JSON payloads.
          const maxHeight = window.innerHeight * 0.98;
          setPaperHeight(`${Math.min(totalHeight, maxHeight)}px`);
        }
      }, 100);

      return () => clearTimeout(timer);
    } else {
      setPaperHeight("auto");
    }
  }, [tabValue, hasJsonData, jsonData]);

  return (
    <DialogCommonDefault
      open={isOpen}
      setOpen={setIsOpen}
      isNeedFixedHeight={false}
      backgroundColor="entity_background"
      maxWidth="md"
      paperStyle={{
        height: paperHeight,
        // Allow taller height and disable internal overflow when JSON tab is active
        maxHeight:
          tabValue === 1 && hasJsonData ? "calc(100vh - 60px)" : "90vh",
        transition: "height 0.3s ease",
        overflowY: tabValue === 1 && hasJsonData ? "visible" : "auto",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px 16px 0 0",
          }}
        >
          <IconButton onClick={() => setIsOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Grid>

        {/* Tabs - styled like reference code */}
        <Grid item xs={12}>
          <div className={classes.tabsContainer}>
            <Tabs
              centered
              value={tabValue}
              onChange={handleTabChange}
              aria-label="log-details-tabs"
              variant="fullWidth"
            >
              <Tab label="DETAILS" {...a11yProps(0)} />
              {hasJsonData && (
                <Tab
                  label="JSON DATA"
                  {...a11yProps(1)}
                  className={classes.jsonTab}
                  style={{ color: "#ff0000" }}
                />
              )}
            </Tabs>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.root}>
            {/* Tab Panel - Details */}
            <TabPanel value={tabValue} index={0}>
              <Grid container>
                {selectedData.message && (
                  <Grid item xs={12}>
                    <div style={{ margin: "28px 28px 0" }}>
                      <CardCommon backgroundColor="entity_background">
                        <Grid container style={{ padding: "16px" }}>
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 600, marginBottom: 8 }}
                            >
                              Message:
                            </Typography>
                            <Typography>{selectedData.message}</Typography>
                          </Grid>
                          {description && (
                            <Grid item xs={12}>
                              <Typography
                                variant="subtitle2"
                                style={{
                                  fontWeight: 600,
                                  marginBottom: 8,
                                  marginTop: "12px",
                                }}
                              >
                                Description:
                              </Typography>
                              <Typography style={{ fontSize: "14px" }}>
                                {description}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardCommon>
                    </div>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <div
                    style={{
                      marginLeft: "28px",
                      marginRight: "28px",
                      marginTop: "28px",
                    }}
                  >
                    <CardCommon backgroundColor={"entity_background"}>
                      <Grid container style={{ padding: "16px" }}>
                        <Grid item xs={6}>
                          <Typography align="left">User Name</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography align="right">{userName}</Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="left">Date</Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="right">
                            {convertDateTimeFormat(selectedData.date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="left">Device</Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="right">
                            {selectedData.deviceId}
                          </Typography>
                        </Grid>
                        {selectedData.extQueOrderNo && (
                          <Grid item xs={6} style={{ marginTop: "4px" }}>
                            <Typography align="left">
                              Ext Que Order No
                            </Typography>
                          </Grid>
                        )}
                        {selectedData.extQueOrderNo && (
                          <Grid item xs={6} style={{ marginTop: "4px" }}>
                            <Typography align="right">
                              {selectedData.extQueOrderNo}
                            </Typography>
                          </Grid>
                        )}
                        {!isGeneric && (
                          <Grid item xs={6} style={{ marginTop: "4px" }}>
                            <Typography align="left">Order No</Typography>
                          </Grid>
                        )}
                        {!isGeneric && (
                          <Grid item xs={6} style={{ marginTop: "4px" }}>
                            <Typography align="right">
                              {selectedData.orderNo}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="left">Display Ref</Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="right">
                            {selectedData.queOrderNo}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="left">Type</Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="right">
                            {selectedData.type}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="left">Value</Typography>
                        </Grid>
                        <Grid item xs={6} style={{ marginTop: "4px" }}>
                          <Typography align="right">
                            {selectedData.value}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardCommon>
                  </div>
                </Grid>

                {selectedData?.type === "kitchen-print" && kitchenPrintData && (
                  <>
                    <Grid item xs={6}>
                      <div
                        style={{
                          marginRight: "28px",
                          marginTop: "28px",
                        }}
                      >
                        <CardCommon backgroundColor={"entity_background"}>
                          <Grid
                            container
                            style={{ padding: "16px", height: "224px" }}
                          >
                            <Grid item xs={6} style={{ marginTop: "24px" }}>
                              <Typography align="left">
                                Print All Order
                              </Typography>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: "24px" }}>
                              <Typography align="right">
                                {kitchenPrintData?.printAllOrder?.toString()
                                  ? kitchenPrintData?.printAllOrder?.toString()
                                  : ""}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: "4px" }}>
                              <Typography align="left">
                                Print Type Only
                              </Typography>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: "4px" }}>
                              <Typography align="right">
                                {kitchenPrintData?.isPrintTypeOnly?.toString()
                                  ? kitchenPrintData?.isPrintTypeOnly?.toString()
                                  : ""}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: "4px" }}>
                              <Typography align="left">Com Type</Typography>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: "4px" }}>
                              <Typography align="right">
                                {kitchenPrintData?.compPrintType}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardCommon>
                      </div>
                    </Grid>
                    {kitchenPrintData?.itemLogs?.length > 0 && (
                      <Grid
                        item
                        xs={12}
                        style={{
                          marginLeft: "28px",
                          marginRight: "28px",
                          marginTop: "28px",
                        }}
                      >
                        <Grid container>
                          <CardCommon backgroundColor={"entity_background"}>
                            {kitchenPrintData?.itemLogs?.map(
                              (item: any, index: any) => (
                                <Grid
                                  container
                                  key={index}
                                  style={{
                                    padding:
                                      index === 0
                                        ? "16px 16px 8px 16px"
                                        : "0px 16px 8px 16px",
                                  }}
                                >
                                  <Grid item xs={12}>
                                    <Typography align="left">{item}</Typography>
                                  </Grid>
                                </Grid>
                              ),
                            )}
                          </CardCommon>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  {Object.values(voidData).length > 0 &&
                    (voidData.type === "void-line" ? (
                      <VoidLine
                        extraInfo={voidData}
                        nodeData={selectedData}
                        userList={userDetails}
                        locationDetailsList={locationSelectorList}
                      />
                    ) : (
                      <VoidOut
                        extraInfo={voidData}
                        userList
                        nodeData={{
                          ...voidData.data,
                          ...selectedData,
                          comments: voidData?.comments ? voidData.comments : "",
                        }}
                        locationDetailsList={locationSelectorList}
                      />
                    ))}
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab Panel - JSON Data */}
            {hasJsonData && (
              <TabPanel value={tabValue} index={1}>
                <Grid container>
                  <Grid item xs={12}>
                    <div
                      style={{
                        margin: "12px 20px",
                      }}
                      ref={jsonContainerRef}
                    >
                      <CardCommon backgroundColor={"entity_background"}>
                        <Grid container style={{ padding: "16px" }}>
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 600, marginBottom: 8 }}
                            >
                              JSON Data:
                            </Typography>
                            <JsonViewer data={jsonData} />
                          </Grid>
                        </Grid>
                      </CardCommon>
                    </div>
                  </Grid>
                </Grid>
              </TabPanel>
            )}
          </div>
        </Grid>
      </Grid>

      <DialogActions style={{ marginBottom: "12px", marginTop: "12px" }}>
        <ButtonCommon
          onClick={() => setIsOpen(false)}
          variant="contained"
          style={{ fontSize: 11, width: "120px", marginRight: "22px" }}
          color={buttonColors.CANCEL_BUTTON_COLOR}
        >
          Cancel
        </ButtonCommon>
      </DialogActions>
    </DialogCommonDefault>
  );
};

export default LogDetailsModal;
